import { z } from 'zod';

export const promptVersionArtifactContentSchema = z.object({
  promptId: z.string().min(1),
  version: z.coerce.number().int().positive(),
  name: z.string().min(1),
});

export type PromptVersionArtifactContent = z.infer<typeof promptVersionArtifactContentSchema>;

/**
 * Parses line-based `key: value` artifact body into a plain object.
 * @param content Trimmed artifact body text
 * @returns Key-value map from non-empty lines with a colon
 */
function parseLineBasedContent(content: string): Record<string, string> {
  const meta: Record<string, string> = {};
  for (const line of content.split('\n')) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();
    if (key.length > 0) {
      meta[key] = value;
    }
  }
  return meta;
}

/**
 * Parses prompt-version artifact content from JSON or YAML-style key-value lines.
 * @param content Raw artifact body
 * @returns Parsed pointer fields or null when invalid
 */
export function parsePromptVersionArtifactContent(
  content: string,
): PromptVersionArtifactContent | null {
  const trimmed = content.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const json: unknown = JSON.parse(trimmed);
    const jsonResult = promptVersionArtifactContentSchema.safeParse(json);
    if (jsonResult.success) {
      return jsonResult.data;
    }
  } catch {
    // fall through to line-based parsing
  }

  const lineResult = promptVersionArtifactContentSchema.safeParse(parseLineBasedContent(trimmed));
  return lineResult.success ? lineResult.data : null;
}

/**
 * Builds a short preview label for prompt-version artifact cards.
 * @param parsed Validated prompt-version pointer content
 * @returns Preview string such as `v3 — Onboarding Prompt`
 */
export function formatPromptVersionArtifactPreview(parsed: PromptVersionArtifactContent): string {
  return `v${parsed.version} — ${parsed.name}`;
}
